import React, { useState, useCallback, useRef } from 'react';
import usePersistentState from '../utils/usePersistentState';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { candidateAPI } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { Spinner, Badge, Pagination } from '../components/UI';

const STATUS_VARIANT = {
  Applied:     'default',
  Shortlisted: 'warning',
  Interview:   'info',
  Rejected:    'danger',
  Hired:       'success',
};

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CandidateFinder() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();

  // Kept for this tab — survives leaving the page and coming back.
  const [query, setQuery]       = usePersistentState('finder:query', '');
  const [results, setResults]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef(null);

  const doSearch = useCallback(async (q, p = 1) => {
    const term = q.trim();
    if (!term) return;
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await candidateAPI.finderSearch({ q: term, page: p, limit: 15 });
      setResults(data.candidates || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPages(data.pages || 0);
    } catch {
      toast.error('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  const handleSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    doSearch(query, 1);
  };

  const handlePageChange = (p) => {
    setPage(p);
    doSearch(query, p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cardBg     = isDark ? 'var(--bg-card)' : '#ffffff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#e8ecf1';

  return (
    <div className="space-y-6">
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Candidate Finder</h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
          Search across all candidates — past and present — by name, email, or phone number.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{
          display: 'flex', gap: 10, alignItems: 'center',
          background: isDark ? 'var(--bg-card)' : '#ffffff',
          border: `1.5px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`,
          borderRadius: 14, padding: '10px 14px',
          boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.2)' : '0 2px 12px rgba(99,102,241,0.08)',
          transition: 'box-shadow 0.2s',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#a5b4fc' : '#6366f1'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name, email address, or phone number…"
            autoFocus
            style={{
              flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none',
              fontSize: 15, color: 'var(--text-primary)',
              fontFamily: 'inherit',
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); setResults([]); setSearched(false); inputRef.current?.focus(); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 4px', fontSize: 16 }}
            >✕</button>
          )}
          <button
            type="submit"
            disabled={!query.trim() || loading}
            style={{
              background: query.trim() ? (isDark ? 'rgba(99,102,241,0.25)' : '#6366f1') : (isDark ? 'rgba(255,255,255,0.05)' : '#e2e8f0'),
              color: query.trim() ? (isDark ? '#c7d2fe' : '#ffffff') : 'var(--text-muted)',
              border: 'none', borderRadius: 9, padding: '7px 18px',
              fontSize: 13, fontWeight: 600, cursor: query.trim() ? 'pointer' : 'not-allowed',
              transition: 'all 0.15s', whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, paddingLeft: 4 }}>
          Tip: you can search partial names, domain-only emails (e.g. "@gmail.com"), or partial phone numbers.
        </p>
      </form>

      {loading && (
        <div className="flex justify-center py-16"><Spinner /></div>
      )}

      {!loading && searched && results.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '48px 24px',
          background: cardBg, border: `1px solid ${cardBorder}`,
          borderRadius: 16,
        }}>
          <p style={{ fontSize: 32, marginBottom: 12 }}>🔍</p>
          <p style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', marginBottom: 6 }}>No candidates found</p>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Try a different name, email, or phone number.
          </p>
        </div>
      )}

      {!loading && results.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', minWidth: 0 }}>
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{total}</span> result{total !== 1 ? 's' : ''} for "<span style={{ fontStyle: 'italic', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{query}</span>"
            </p>
            {pages > 1 && (
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Page {page} of {pages}</p>
            )}
          </div>

          <div className="space-y-3">
            {results.map(c => (
              <button
                key={c.id}
                onClick={() => navigate(`/candidate-profile/${c.id}`, { state: { from: '/candidate-finder', backLabel: 'Back to Finder' } })}
                style={{
                  width: '100%', textAlign: 'left', cursor: 'pointer',
                  background: cardBg,
                  border: `1px solid ${cardBorder}`,
                  borderRadius: 14, padding: '16px 20px',
                  display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
                  transition: 'all 0.15s ease',
                  boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.04)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = isDark ? 'rgba(99,102,241,0.4)' : '#a5b4fc';
                  e.currentTarget.style.boxShadow   = isDark ? '0 4px 20px rgba(0,0,0,0.25)' : '0 4px 16px rgba(99,102,241,0.1)';
                  e.currentTarget.style.transform   = 'translateY(-1px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = cardBorder;
                  e.currentTarget.style.boxShadow   = isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.04)';
                  e.currentTarget.style.transform   = 'none';
                }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  background: c.isDeleted
                    ? (isDark ? 'rgba(100,116,139,0.3)' : '#e2e8f0')
                    : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: c.isDeleted ? 'var(--text-muted)' : '#fff',
                  fontWeight: 700, fontSize: 16,
                  boxShadow: c.isDeleted ? 'none' : '0 2px 8px rgba(99,102,241,0.3)',
                }}>
                  {c.fullName?.[0]?.toUpperCase()}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{c.fullName}</span>
                    <Badge variant={STATUS_VARIANT[c.status] || 'default'}>{c.status}</Badge>
                    {c.isDeleted && (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 99, background: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2', color: isDark ? '#fca5a5' : '#dc2626', border: `1px solid ${isDark ? 'rgba(239,68,68,0.3)' : '#fecaca'}` }}>
                        Archived
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 12, color: 'var(--text-secondary)' }}>
                    <span style={{ display: 'inline-block', maxWidth: '100%', wordBreak: 'break-all', overflowWrap: 'anywhere', minWidth: 0 }}>✉ {c.email}</span>
                    {c.phone && <span>📞 {c.phone}</span>}
                    {c.client?.clientName && <span>🏢 {c.client.clientName}</span>}
                    {c.requirement?.jobTitle && <span>📋 {c.requirement.jobTitle}</span>}
                    <span>💼 {c.experience} yrs{c.currentRole ? ` · ${c.currentRole}` : ''}</span>
                  </div>
                  {c.skills?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                      {c.skills.slice(0, 5).map(s => (
                        <span key={s} style={{
                          fontSize: 10, padding: '2px 7px', borderRadius: 99,
                          background: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff',
                          color: isDark ? '#a5b4fc' : '#4f46e5',
                          border: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : '#c7d2fe'}`,
                        }}>{s}</span>
                      ))}
                      {c.skills.length > 5 && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{c.skills.length - 5}</span>}
                    </div>
                  )}
                </div>

                <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 0 }}>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDate(c.createdAt)}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6, justifyContent: 'flex-end', color: isDark ? '#818cf8' : '#6366f1', fontSize: 12, fontWeight: 500 }}>
                    View profile
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <Pagination page={page} pages={pages} onPage={handlePageChange} />
        </>
      )}
    </div>
  );
}
